/**
 * GenerateMealPlan Component
 * Handles meal plan generation workflow
 */

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ProfileSelector } from "./ProfileSelector";
import { MacroTargeting } from "./MacroTargeting";
import FreeRunsIndicator from "./FreeRunsIndicator";
import { Profile, mealPlanApi, CreateMealPlanData, userApi } from "../services/api";
import { LLMMacros } from "../types/common";
import { useAuth } from "../contexts/AuthContext";
import { usePassioApi } from "../hooks/usePassioApi";
import { useToast } from "../contexts/ToastContext";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";
import {
    MealPlanPreview as MealPlanPreviewType,
} from "../types/mealPlanning";
import { tokenUsageTracker } from "../services/tokenUsageTracker";
import { toTitleCase } from "../utils/textFormatting";
import { getUserAccessStatus, hasServiceAccess, getUserApiConfig, addDayCountReinforcement } from "../utils";
import { useSubscription } from "../contexts/SubscriptionContext";
import { taskWorkerApi } from "../services/api";
import { APP_CONFIG } from "../config/app.config";

export const GenerateMealPlan: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { dbUser, refreshUser } = useAuth();
    const { showToast } = useToast();
    const { addTask, updateTask, getActiveTasksCount } = useBackgroundTasks();
    const { client, isUsingFreeTrial, generateMealPlanWithTask } = usePassioApi();
    const { openSubscriptionModal } = useSubscription();

    // Check if user has access to generate meal plans
    const hasAccess = hasServiceAccess(dbUser);
    const accessStatus = getUserAccessStatus(dbUser);

    const [selectedProfiles, setSelectedProfiles] = useState<Profile[]>([]);
    const [showMacroTargeting, setShowMacroTargeting] = useState(false);
    const [showProfileData, setShowProfileData] = useState(false);
    const [macroTargets, setMacroTargets] = useState<Partial<LLMMacros>>({});
    const [userInput, setUserInput] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showBackgroundModal, setShowBackgroundModal] = useState(false);
    const [numberOfDays, setNumberOfDays] = useState(5); // Default to 5 days
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [previewData, setPreviewData] = useState<MealPlanPreviewType | null>(
        null
    );
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['duration', 'instructions']));
    // Removed promptType - only using custom instructions for now
    // Removed guided prompts - not implemented yet

    const apiConfig = getUserApiConfig(dbUser);

    const getApiParams = useCallback(() => {
        const params: any = {};

        // Add model parameters from user's API configuration
        if (apiConfig.model) {
            params.model = apiConfig.model;
        }

        if (apiConfig.refinementModel) {
            params.refinementmodel = apiConfig.refinementModel;
        }

        return params;
    }, [apiConfig]);

    // Handle template prompt from navigation state
    useEffect(() => {
        if (location.state?.templatePrompt) {
            setUserInput(location.state.templatePrompt);
            // Clear the navigation state to prevent re-triggering
            window.history.replaceState({}, document.title);

            // Focus and scroll to end of textarea after a short delay
            setTimeout(() => {
                const textarea = document.querySelector("textarea");
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(
                        textarea.value.length,
                        textarea.value.length
                    );
                    textarea.scrollTop = textarea.scrollHeight;
                }
            }, 100);
        }
        
        // Check if profile was selected from navigation state
        if (location.state?.selectedProfileId) {
            // ProfileSelector will handle this automatically
            // Just expand the profile section
            setExpandedSections(prev => {
                const newSet = new Set(prev);
                newSet.add('profile');
                return newSet;
            });
        }
    }, [location.state]);

    // Check if free trial user can submit a new task
    const canFreeTrialUserSubmit = useCallback(() => {
        const accessStatus = getUserAccessStatus(dbUser);
        
        // If user is on free trial (has free runs but no subscription)
        if (accessStatus.hasFreeRuns && !accessStatus.isPremium && !accessStatus.isTrial && !accessStatus.hasApiKey) {
            // Check if there are any active tasks
            const activeTasksCount = getActiveTasksCount();
            return activeTasksCount === 0;
        }
        
        // All other users can submit multiple tasks
        return true;
    }, [dbUser, getActiveTasksCount]);

    const handleProfileSelection = useCallback((profiles: Profile[]) => {
        setSelectedProfiles(profiles);
    }, []);

    // Removed unused handleMacroTargetsChange - we now handle this inline

    const toggleSection = (section: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    // Helper function to generate strong day number reinforcement
    const getDayNumberReinforcement = (days: number): string => {
        const dayWord = days === 1 ? "day" : "days";
        
        return `Generate a ${days}-${dayWord} meal plan.
Each day must include breakfast, lunch, dinner, and snacks.
The mealPlanDays array must contain exactly ${days} elements.`;
    };

    const buildPrompt = useCallback((): string => {
        let prompt = `${getDayNumberReinforcement(numberOfDays)}\n\n`;

        // Add profile data (with macro targets stripped if overridden)
        if (selectedProfiles.length > 0) {
            const profileData = { ...selectedProfiles[0] };
            
            // Strip macro targets from profile if they're being overridden
            if (showMacroTargeting && Object.keys(macroTargets).length > 0 && profileData.profile_data?.macro_targets) {
                const profileDataCopy = { ...profileData };
                profileDataCopy.profile_data = { ...profileData.profile_data };
                delete profileDataCopy.profile_data.macro_targets;
                profileData.profile_data = profileDataCopy.profile_data;
            }

            // Check if profile has likes or dislikes
            const hasLikes =
                profileData.profile_data?.likes &&
                profileData.profile_data.likes.trim().length > 0;
            const hasDislikes =
                profileData.profile_data?.dislikes &&
                profileData.profile_data.dislikes.trim().length > 0;

            prompt += `Profile: ${JSON.stringify(profileData, null, 2)}\n`;

            // Add note about likes/dislikes if they exist
            if (hasLikes || hasDislikes) {
                prompt += `\nNote: Use the profile's likes and dislikes as guidance for meal types and flavors.\n`;
            }

            prompt += `\n`;
        }

        // Add macro targets if any are enabled
        if (showMacroTargeting && Object.keys(macroTargets).length > 0) {
            prompt += `Macro Targets: ${JSON.stringify(
                macroTargets,
                null,
                2
            )}\n\n`;
        }

        // Add user input
        if (userInput.trim()) {
            prompt += `Additional Instructions: ${userInput}\n`;
        }

        // Add day count reinforcement at both ends
        return addDayCountReinforcement(prompt, numberOfDays);
    }, [selectedProfiles, macroTargets, userInput, numberOfDays, showMacroTargeting]);

    const handleGeneratePreview = async () => {
        if (!dbUser?.id) {
            setError("You must be logged in to generate meal plan previews");
            return;
        }

        if (!client) {
            setError("API client not initialized");
            return;
        }

        if (selectedProfiles.length === 0) {
            setError("Please select a profile");
            return;
        }

        if (userInput.trim() === "") {
            setError("Please provide instructions for your meal plan");
            return;
        }

        // Check if free trial user can submit
        if (!canFreeTrialUserSubmit()) {
            setShowFreeTrialModal(true);
            return;
        }

        setIsGeneratingPreview(true);
        setError(null);
        setPreviewError(null);

        // Smooth scroll to top
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

        // Show generation start toast
        showToast({
            message: "Generating meal plan preview...",
            type: "info",
            duration: 3000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "meal-plan-preview",
            status: "running",
            message: "Generating meal plan preview",
        });

        try {
            const prompt = buildPrompt();
            const queryParams = getApiParams();

            // Decrement free run immediately if user is on free trial
            if (dbUser && !dbUser.passioapikey && dbUser.free_runs_remaining > 0) {
                try {
                    await userApi.decrementFreeRun(dbUser.id);
                    // Refresh user data to get updated count
                    await refreshUser();
                } catch (error) {
                    console.error('Failed to decrement free run:', error);
                    // Continue with generation even if decrement fails
                }
            }

            // Call the Passio API with model parameters
            const previewResult = await client.generateMealPlanPreview(
                prompt,
                {},
                queryParams,
                {
                    ttl: 0, // No caching for preview generation
                }
            );

            setPreviewData(previewResult);
            setIsPreviewMode(true);

            // Check if we have token usage data
            const usageData = tokenUsageTracker.getUsageData();
            if (usageData && usageData.requestUsage > 0) {
                // Show token usage in a separate toast
                setTimeout(() => {
                    showToast({
                        message: `Used ${usageData.requestUsage.toLocaleString()} tokens for this preview`,
                        type: "info",
                        duration: 5000,
                    });
                }, 500);
            }

            // Update task status
            updateTask(taskId, {
                status: "completed",
                completedAt: new Date(),
                tokenUsage: usageData?.requestUsage,
            });

            // Show success toast
            showToast({
                message: "Meal plan preview generated successfully!",
                type: "success",
                duration: 3000,
            });
        } catch (err) {
            console.error("Error generating meal plan preview:", err);
            setPreviewError(
                err instanceof Error
                    ? err.message
                    : "Failed to generate meal plan preview"
            );

            // Get token usage data for error tracking
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "failed",
                completedAt: new Date(),
                error:
                    err instanceof Error
                        ? err.message
                        : "Failed to generate meal plan preview",
                tokenUsage: usageData?.requestUsage,
            });

            // Show error toast
            showToast({
                message:
                    err instanceof Error
                        ? err.message
                        : "Failed to generate meal plan preview",
                type: "error",
                duration: 5000,
            });
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleGenerateMealPlan = () => {
        if (!dbUser?.id) {
            setError("You must be logged in to generate meal plans");
            return;
        }

        if (!client) {
            setError("API client not initialized");
            return;
        }

        if (selectedProfiles.length === 0) {
            setError("Please select a profile");
            return;
        }

        if (userInput.trim() === "") {
            setError("Please provide instructions for your meal plan");
            return;
        }

        // Check if free trial user can submit
        if (!canFreeTrialUserSubmit()) {
            setShowFreeTrialModal(true);
            return;
        }

        // Show background task modal
        setShowBackgroundModal(true);
        setIsGenerating(true);
        setError(null);

        // Show generation start toast
        showToast({
            message: `Generating ${numberOfDays}-day meal plan...`,
            type: "info",
            duration: 5000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "meal-plan-generation",
            status: "running",
            message: `Generating meal plan for ${selectedProfiles[0].name}`,
        });

        // Generate meal plan in background
        const generateInBackground = async () => {
            if (!client) {
                console.error("API client not initialized");
                return;
            }
            
            try {
                const prompt = buildPrompt();
                const queryParams = getApiParams();

                // Build the generation URL for tracking
                const baseUrl = process.env.REACT_APP_API_URL || "";
                const searchParams = new URLSearchParams(queryParams);
                const generationUrl = `${baseUrl}/generate-meal-plan${
                    searchParams.toString() ? `?${searchParams.toString()}` : ""
                }`;

                // Decrement free run immediately if user is on free trial
                if (dbUser && !dbUser.passioapikey && dbUser.free_runs_remaining > 0) {
                    try {
                        await userApi.decrementFreeRun(dbUser.id);
                        // Refresh user data to get updated count
                        await refreshUser();
                    } catch (error) {
                        console.error('Failed to decrement free run:', error);
                        // Continue with generation even if decrement fails
                    }
                }

                // Track token usage before the call
                // Use task-based generation
                const result = await generateMealPlanWithTask(
                    prompt,
                    {
                        profileId: selectedProfiles.length === 1 ? selectedProfiles[0].id : null,
                        numberOfDays: numberOfDays
                    },
                    queryParams,
                    true // useTaskWorker = true
                );

                // Check if this is a task submission response
                if (result.id && (result.status === "pending" || result.status === "submitted")) {
                    // Update the background task with the taskId for polling
                    updateTask(taskId, {
                        taskWorkerId: result.id,
                        status: "running",
                        // Link to meal plan history page since we don't have the specific ID yet
                        actionUrl: `/meal-planner/history`,
                    });
                    
                    // IMPORTANT: The task is now tracked by taskWorkerId, so the sync won't create a duplicate
                    
                    // Show success message for task submission
                    showToast({
                        message: "Meal plan generation has been submitted. It will be added to your meal plans when complete.",
                        type: "success",
                        duration: 7000,
                    });
                    
                    // Start polling for task completion
                    pollForTaskCompletion(result.id, taskId);
                    
                    // Reset form
                    setIsGenerating(false);
                    return;
                }

                // If we got here, it means we used synchronous generation (shouldn't happen with useTaskWorker=true)
                const mealPlanResult = result;
                const beforeUsage = tokenUsageTracker.getUsageData();

                // Calculate token usage for this request
                const afterUsage = tokenUsageTracker.getUsageData();
                const tokensUsed =
                    (afterUsage?.requestUsage || 0) -
                    (beforeUsage?.requestUsage || 0);

                // Create meal plan data for internal API
                const now = new Date();
                const timeStr = now.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });
                const createData: CreateMealPlanData = {
                    name: `Meal Plan - ${now.toLocaleDateString()} ${timeStr}`,
                    profiles: selectedProfiles.map((p) => p.id),
                    plan: mealPlanResult as any, // The LLMMealPlanResultOutgoing
                    generation_prompt: prompt,
                    generation_url: generationUrl,
                    token_count: tokensUsed > 0 ? tokensUsed : undefined,
                };

                // Save to database
                const savedMealPlan = await mealPlanApi.createMealPlan(
                    dbUser.id!,
                    createData
                );

                // Check if we have token usage data
                const usageData = tokenUsageTracker.getUsageData();
                if (usageData && usageData.requestUsage > 0) {
                    // Show token usage in a separate toast
                    setTimeout(() => {
                        showToast({
                            message: `Used ${usageData.requestUsage.toLocaleString()} tokens for this meal plan`,
                            type: "info",
                            duration: 5000,
                        });
                    }, 500);
                }

                // Update task status
                updateTask(taskId, {
                    status: "completed",
                    completedAt: new Date(),
                    result: savedMealPlan,
                    actionUrl: `/meal-planner/editor?id=${savedMealPlan.id}`,
                    viewed: false,
                    tokenUsage: usageData?.requestUsage,
                });

                // Show success toast with action
                showToast({
                    message: "Meal plan generated successfully!",
                    type: "success",
                    duration: 10000,
                    action: {
                        label: "View Plan",
                        onClick: () => {
                            // Navigate to editor with meal plan ID
                            navigate(
                                `/meal-planner/editor?id=${savedMealPlan.id}`
                            );
                        },
                    },
                });
            } catch (err) {
                console.error("Error generating meal plan:", err);

                // Get token usage data for error tracking
                const usageData = tokenUsageTracker.getUsageData();

                // Update task status
                updateTask(taskId, {
                    status: "failed",
                    completedAt: new Date(),
                    error:
                        err instanceof Error
                            ? err.message
                            : "Failed to generate meal plan",
                    tokenUsage: usageData?.requestUsage,
                });

                // Show error toast
                showToast({
                    message:
                        err instanceof Error
                            ? err.message
                            : "Failed to generate meal plan",
                    type: "error",
                    duration: 10000,
                });
            }
        };

        // Poll for task completion
        const pollForTaskCompletion = async (taskWorkerId: string, backgroundTaskId: string) => {
            const pollInterval = 15000; // Poll every 15 seconds
            const maxPolls = 50; // Max 5 minutes (150 * 15 seconds)
            let pollCount = 0;

            const poll = async () => {
                try {
                    // Check the task worker event
                    const taskEvent = await taskWorkerApi.getTaskWorkerEvent(taskWorkerId);
                    
                    if (!taskEvent) {
                        console.error(`Task ${taskWorkerId} not found`);
                        updateTask(backgroundTaskId, {
                            status: "failed",
                            completedAt: new Date(),
                            error: "Task not found",
                        });
                        return;
                    }

                    // Check if task is completed
                    if (taskEvent.status === "completed") {
                        // Task is complete!
                        updateTask(backgroundTaskId, {
                            status: "completed",
                            completedAt: new Date(),
                            // Still link to history page since meal plan creation happens asynchronously
                            actionUrl: `/meal-planner/history`,
                            viewed: false,
                        });

                        showToast({
                            message: "Meal plan generated successfully! Check your meal plan history.",
                            type: "success",
                            duration: 10000,
                            action: {
                                label: "View History",
                                onClick: () => {
                                    navigate("/meal-planner/history");
                                },
                            },
                        });
                        return;
                    }

                    // Check if task failed
                    if (taskEvent.status === "failed") {
                        updateTask(backgroundTaskId, {
                            status: "failed",
                            completedAt: new Date(),
                            error: taskEvent.event_data?.error || "Meal plan generation failed",
                        });

                        showToast({
                            message: taskEvent.event_data?.error || "Failed to generate meal plan. Please try again.",
                            type: "error",
                            duration: 10000,
                        });
                        return;
                    }

                    // Continue polling if not complete and under max polls
                    pollCount++;
                    if (pollCount < maxPolls) {
                        setTimeout(poll, pollInterval);
                    } else {
                        // Timeout
                        updateTask(backgroundTaskId, {
                            status: "failed",
                            completedAt: new Date(),
                            error: "Meal plan generation timed out",
                        });
                        
                        showToast({
                            message: "Meal plan generation is taking longer than expected. Please check your meal plan history later.",
                            type: "warning",
                            duration: 10000,
                        });
                    }
                } catch (err) {
                    console.error("Error polling for task completion:", err);
                    
                    // Don't fail immediately on polling errors, retry a few times
                    pollCount++;
                    if (pollCount < 5) {
                        setTimeout(poll, pollInterval);
                    } else {
                        updateTask(backgroundTaskId, {
                            status: "failed",
                            completedAt: new Date(),
                            error: "Failed to check task status",
                        });
                    }
                }
            };

            // Start polling
            setTimeout(poll, pollInterval);
        };

        // Start generation in background
        generateInBackground();

        // Hide modal after 10 seconds
        setTimeout(() => {
            setShowBackgroundModal(false);
        }, 10000);

        // Reset form immediately (but keep selectedProfiles since ProfileSelector manages its own state)
        setIsGenerating(false);
        setUserInput("");
        setMacroTargets({});
        setShowMacroTargeting(false);
        setShowProfileData(false);
        setError(null);
        // Keep numberOfDays as user preference - don't reset it

        // Don't clear selectedProfiles - let ProfileSelector maintain its own selection
    };

    const containerStyle: React.CSSProperties = {
        maxWidth: "900px",
        margin: "0 auto",
        padding: "24px",
    };

    const pageHeaderStyle: React.CSSProperties = {
        textAlign: "center",
        marginBottom: "40px",
    };

    const pageTitleStyle: React.CSSProperties = {
        fontSize: "32px",
        fontWeight: "700",
        color: "#1f2937",
        marginBottom: "8px",
    };

    const pageSubtitleStyle: React.CSSProperties = {
        fontSize: "16px",
        color: "#6b7280",
        marginBottom: "0",
    };

    const cardStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "16px",
        padding: "24px",
        marginBottom: "24px",
        boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        border: "1px solid #f3f4f6",
    };

    const sectionStyle: React.CSSProperties = {
        marginBottom: "32px",
    };

    const labelStyle: React.CSSProperties = {
        display: "block",
        fontSize: "18px",
        fontWeight: 600,
        color: "#1f2937",
        marginBottom: "12px",
    };

    // Removed unused style variables

    const collapsibleHeaderStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        backgroundColor: "#f8fafc",
        border: "2px solid #e2e8f0",
        borderRadius: "12px",
        cursor: "pointer",
        marginBottom: "20px",
        transition: "all 0.2s",
    };

    const chevronStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#64748b",
        transition: "transform 0.2s",
    };

    const textAreaStyle: React.CSSProperties = {
        width: "100%",
        minHeight: "220px",
        padding: "16px",
        border: "2px solid #e2e8f0",
        borderRadius: "12px",
        fontSize: "15px",
        lineHeight: "1.6",
        resize: "vertical",
        outline: "none",
        transition: "all 0.2s",
        fontFamily: "inherit",
    };

    const preStyle: React.CSSProperties = {
        backgroundColor: "#f8fafc",
        padding: "20px",
        borderRadius: "12px",
        border: "2px solid #e2e8f0",
        fontSize: "13px",
        overflow: "auto",
        maxHeight: "400px",
        fontFamily:
            "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
        lineHeight: "1.5",
    };

    const buttonContainerStyle: React.CSSProperties = {
        display: "flex",
        gap: "20px",
        justifyContent: "center",
        marginTop: "32px",
        position: "relative",
    };

    const buttonStyle: React.CSSProperties = {
        padding: "16px 32px",
        borderRadius: "12px",
        border: "none",
        fontSize: "16px",
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        transition: "all 0.2s",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    };

    const primaryButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        backgroundColor: "#3b82f6",
        color: "white",
        transform: "scale(1.05)",
        fontWeight: "700",
    };

    const secondaryButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        backgroundColor: "#64748b",
        color: "white",
    };

    const disabledButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        backgroundColor: "#e5e7eb",
        color: "#9ca3af",
        cursor: "not-allowed",
        boxShadow: "none",
    };

    const loadingOverlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    };

    const loadingBoxStyle: React.CSSProperties = {
        backgroundColor: "white",
        padding: "32px",
        borderRadius: "8px",
        textAlign: "center",
    };

    // Preview meal management
    const [previewMeals, setPreviewMeals] = useState<
        Array<{
            id: string;
            dayNumber: number;
            mealType: string;
            recipeName: string;
            rejected: boolean;
            notes: string;
        }>
    >([]);
    const [previewNotes, setPreviewNotes] = useState("");
    const [showMealPlanInfo, setShowMealPlanInfo] = useState(false);

    // Extract meals from preview data when it changes
    useEffect(() => {
        if (!previewData) {
            setPreviewMeals([]);
            return;
        }

        const extractedMeals: Array<{
            id: string;
            dayNumber: number;
            mealType: string;
            recipeName: string;
            rejected: boolean;
            notes: string;
        }> = [];

        previewData.mealPlanDays.forEach((day, dayIndex) => {
            const mealTypes = [
                "breakfast",
                "lunch",
                "dinner",
                "snack",
            ] as const;

            mealTypes.forEach((mealType) => {
                const mealSlot = day[mealType];
                mealSlot.recipes.forEach((recipe, recipeIndex) => {
                    extractedMeals.push({
                        id: `${dayIndex}-${mealType}-${recipeIndex}`,
                        dayNumber: dayIndex + 1,
                        mealType:
                            mealType.charAt(0).toUpperCase() +
                            mealType.slice(1),
                        recipeName: toTitleCase(recipe.name),
                        rejected: recipe.rejected,
                        notes: "",
                    });
                });
            });
        });

        setPreviewMeals(extractedMeals);
        setPreviewNotes("");
    }, [previewData]);

    const handlePreviewMealToggle = (mealId: string) => {
        setPreviewMeals((prev) =>
            prev.map((meal) =>
                meal.id === mealId
                    ? { ...meal, rejected: !meal.rejected }
                    : meal
            )
        );
    };

    const handlePreviewMealNotesChange = (mealId: string, notes: string) => {
        setPreviewMeals((prev) =>
            prev.map((meal) => (meal.id === mealId ? { ...meal, notes } : meal))
        );
    };

    const handleBackToGenerate = () => {
        setIsPreviewMode(false);
        setPreviewData(null);
        setPreviewMeals([]);
        setPreviewNotes("");
        setPreviewError(null);
        setShowMealPlanInfo(false);
    };

    const buildPreviewResponseData = () => {
        if (!previewData) return null;

        // Create the response data with updated rejected status
        const updatedMealPlanDays = previewData.mealPlanDays.map(
            (day, dayIndex) => {
                const updatedDay = { ...day };
                const mealTypes = [
                    "breakfast",
                    "lunch",
                    "dinner",
                    "snack",
                ] as const;

                mealTypes.forEach((mealType) => {
                    updatedDay[mealType] = {
                        recipes: day[mealType].recipes.map(
                            (recipe, recipeIndex) => {
                                const mealId = `${dayIndex}-${mealType}-${recipeIndex}`;
                                const previewMeal = previewMeals.find(
                                    (m) => m.id === mealId
                                );

                                const updatedRecipe: any = {
                                    ...recipe,
                                    rejected:
                                        previewMeal?.rejected ??
                                        recipe.rejected,
                                };

                                // Add notes if they exist
                                if (previewMeal?.notes?.trim()) {
                                    updatedRecipe.notes =
                                        previewMeal.notes.trim();
                                }

                                return updatedRecipe;
                            }
                        ),
                    };
                });

                return updatedDay;
            }
        );

        return {
            ...previewData,
            mealPlanDays: updatedMealPlanDays,
        };
    };

    const handleResubmitPreview = async () => {
        const responseData = buildPreviewResponseData();
        if (!responseData) return;

        if (!client) {
            setPreviewError("API client not initialized");
            return;
        }

        // Check if free trial user can submit
        if (!canFreeTrialUserSubmit()) {
            setShowFreeTrialModal(true);
            return;
        }

        setIsGeneratingPreview(true);
        setPreviewError(null);

        // Smooth scroll to top after state update
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }, 100);

        // Show generation start toast
        showToast({
            message: "Regenerating meal plan preview...",
            type: "info",
            duration: 3000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "meal-plan-preview",
            status: "running",
            message: "Regenerating meal plan preview",
        });

        try {
            const queryParams = getApiParams();

            // Call the preview API with the updated response data
            const previewContent =
                JSON.stringify(responseData) +
                "\n\n" +
                (previewNotes.trim() ||
                    "Regenerate the meal plan preview with the updated preferences.");
            const previewResult = await client.generateMealPlanPreview(
                previewContent,
                {},
                queryParams,
                {
                    ttl: 0, // No caching for preview generation
                }
            );

            setPreviewData(previewResult);

            // Check if we have token usage data
            const usageData = tokenUsageTracker.getUsageData();
            if (usageData && usageData.requestUsage > 0) {
                // Show token usage in a separate toast
                setTimeout(() => {
                    showToast({
                        message: `Used ${usageData.requestUsage.toLocaleString()} tokens for this preview`,
                        type: "info",
                        duration: 5000,
                    });
                }, 500);
            }

            // Update task status
            updateTask(taskId, {
                status: "completed",
                completedAt: new Date(),
                tokenUsage: usageData?.requestUsage,
            });

            // Show success toast
            showToast({
                message: "Meal plan preview regenerated successfully!",
                type: "success",
                duration: 3000,
            });
        } catch (err) {
            console.error("Error regenerating meal plan preview:", err);
            setPreviewError(
                err instanceof Error
                    ? err.message
                    : "Failed to regenerate meal plan preview"
            );

            // Get token usage data for error tracking
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "failed",
                completedAt: new Date(),
                error:
                    err instanceof Error
                        ? err.message
                        : "Failed to regenerate meal plan preview",
                tokenUsage: usageData?.requestUsage,
            });

            // Show error toast
            showToast({
                message:
                    err instanceof Error
                        ? err.message
                        : "Failed to regenerate meal plan preview",
                type: "error",
                duration: 5000,
            });
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleGenerateFromPreview = async () => {
        const responseData = buildPreviewResponseData();
        if (!responseData) return;

        // Check if free trial user can submit
        if (!canFreeTrialUserSubmit()) {
            setShowFreeTrialModal(true);
            return;
        }

        // Show background task modal
        setShowBackgroundModal(true);
        setIsGenerating(true);
        setError(null);

        // Show generation start toast
        showToast({
            message: `Generating ${numberOfDays}-day meal plan from preview...`,
            type: "info",
            duration: 5000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "meal-plan-generation",
            status: "running",
            message: `Generating meal plan from preview`,
        });

        // Generate meal plan in background
        const generateInBackground = async () => {
            if (!client) {
                console.error("API client not initialized");
                return;
            }
            
            try {
                const queryParams = getApiParams();

                // Build the generation URL for tracking
                const baseUrl = process.env.REACT_APP_API_URL || "";
                const searchParams = new URLSearchParams(queryParams);
                const generationUrl = `${baseUrl}/generate-meal-plan${
                    searchParams.toString() ? `?${searchParams.toString()}` : ""
                }`;

                // Decrement free run immediately if user is on free trial
                if (dbUser && !dbUser.passioapikey && dbUser.free_runs_remaining > 0) {
                    try {
                        await userApi.decrementFreeRun(dbUser.id);
                        // Refresh user data to get updated count
                        await refreshUser();
                    } catch (error) {
                        console.error('Failed to decrement free run:', error);
                        // Continue with generation even if decrement fails
                    }
                }

                // Track token usage before the call
                const beforeUsage = tokenUsageTracker.getUsageData();

                // Call the meal plan API with the updated response data
                const mealPlanContent =
                    JSON.stringify(responseData) +
                    "\n\n" +
                    (previewNotes.trim() ||
                        "Generate the final meal plan based on the preview preferences.");
                const mealPlanResult = await client.generateMealPlan(
                    mealPlanContent,
                    {},
                    queryParams,
                    {
                        ttl: 0, // No caching for meal plan generation
                    }
                );

                // Calculate token usage for this request
                const afterUsage = tokenUsageTracker.getUsageData();
                const tokensUsed =
                    (afterUsage?.requestUsage || 0) -
                    (beforeUsage?.requestUsage || 0);

                // Create meal plan data for internal API
                const now = new Date();
                const timeStr = now.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });
                const createData: CreateMealPlanData = {
                    name: `Meal Plan - ${now.toLocaleDateString()} ${timeStr}`,
                    profiles: selectedProfiles.map((p) => p.id),
                    plan: mealPlanResult as any,
                    generation_prompt: mealPlanContent,
                    generation_url: generationUrl,
                    token_count: tokensUsed > 0 ? tokensUsed : undefined,
                };

                // Save to database
                const savedMealPlan = await mealPlanApi.createMealPlan(
                    dbUser!.id!,
                    createData
                );

                // Check if we have token usage data
                const usageData = tokenUsageTracker.getUsageData();
                if (usageData && usageData.requestUsage > 0) {
                    // Show token usage in a separate toast
                    setTimeout(() => {
                        showToast({
                            message: `Used ${usageData.requestUsage.toLocaleString()} tokens for this meal plan`,
                            type: "info",
                            duration: 5000,
                        });
                    }, 500);
                }

                // Update task status
                updateTask(taskId, {
                    status: "completed",
                    completedAt: new Date(),
                    result: savedMealPlan,
                    actionUrl: `/meal-planner/editor?id=${savedMealPlan.id}`,
                    viewed: false,
                    tokenUsage: usageData?.requestUsage,
                });

                // Show success toast with action
                showToast({
                    message: "Meal plan generated successfully!",
                    type: "success",
                    duration: 10000,
                    action: {
                        label: "View Plan",
                        onClick: () => {
                            navigate(
                                `/meal-planner/editor?id=${savedMealPlan.id}`
                            );
                        },
                    },
                });

                // Reset preview mode
                handleBackToGenerate();
            } catch (err) {
                console.error("Error generating meal plan from preview:", err);

                // Get token usage data for error tracking
                const usageData = tokenUsageTracker.getUsageData();

                // Update task status
                updateTask(taskId, {
                    status: "failed",
                    completedAt: new Date(),
                    error:
                        err instanceof Error
                            ? err.message
                            : "Failed to generate meal plan from preview",
                    tokenUsage: usageData?.requestUsage,
                });

                // Show error toast
                showToast({
                    message:
                        err instanceof Error
                            ? err.message
                            : "Failed to generate meal plan from preview",
                    type: "error",
                    duration: 10000,
                });
            }
        };

        // Start generation in background
        generateInBackground();

        // Hide modal after 10 seconds
        setTimeout(() => {
            setShowBackgroundModal(false);
        }, 10000);

        // Reset form state
        setIsGenerating(false);
        setError(null);

        // Reset back to generate form view
        handleBackToGenerate();
    };

    const placeholderText = `Example instructions:

• Create a Mediterranean-inspired meal plan with fresh ingredients
• Focus on high protein (30g+ per meal) for muscle building
• Include vegetarian dinners 3 times per week
• Keep breakfast quick and simple (under 15 minutes)
• Add meal prep tips for Sunday batch cooking
• Avoid dairy and gluten throughout

Be specific about your preferences, restrictions, and goals!`;

    const examplePrompts = [
        {
            title: "🏋️ Active Athlete",
            category: "Fitness",
            prompt: "I'm training for a marathon and lifting weights 4x per week. Need high protein meals with complex carbs for energy. Include pre-workout snacks and post-workout recovery meals. Prefer chicken, fish, Greek yogurt, and plant proteins. Keep meals interesting with variety and different flavor profiles."
        },
        {
            title: "🌱 Plant-Based Beginner",
            category: "Dietary",
            prompt: "New to vegetarian eating. Need balanced meals with complete proteins from beans, lentils, quinoa, and tofu. Include B12 and iron-rich foods. Make it tasty with global cuisines - Indian, Mediterranean, Mexican. Simple recipes that take less than 30 minutes to prepare."
        },
        {
            title: "👨‍👩‍👧‍👦 Busy Family of 4",
            category: "Lifestyle",
            prompt: "Need family meals that work for parents and 2 picky kids (ages 6 and 9). Budget-conscious approach. Kids love pasta, tacos, and chicken nuggets - make healthier versions. Hide vegetables creatively. Quick weeknight dinners that take 30 minutes or less."
        },
        {
            title: "💼 Office Worker Wellness",
            category: "Health",
            prompt: "Sedentary desk job, want to eat healthier. Need filling, high-fiber meals to stay satisfied. Prefer portable lunches and healthy snacks to avoid vending machine temptations. Focus on colorful vegetables and whole foods. Enjoy trying new recipes on weekends."
        },
        {
            title: "🍕 Mediterranean Style",
            category: "Health",
            prompt: "Love Italian and Greek food. Prefer olive oil, fish twice per week, whole grains, and nuts. Limit red meat to once weekly. Want flavorful meals with herbs and spices that don't feel restrictive. Enjoy cooking with wine and fresh ingredients."
        },
        {
            title: "🎓 College Student",
            category: "Lifestyle",
            prompt: "Living in a dorm with just a microwave and mini fridge. Limited budget. Need no-cook breakfasts, microwave-friendly dinners, and easy snacks for studying. Want to eat healthier than just instant noodles. Simple ingredients available at campus store."
        },
        {
            title: "🤰 Pregnancy Nutrition",
            category: "Health",
            prompt: "Second trimester, need extra folate, iron, and calcium. Managing morning sickness with small frequent meals. Avoiding raw fish, deli meats, and soft cheeses. Craving both sweet and salty foods. Include healthy snacks for work and energy-boosting options."
        },
        {
            title: "🌾 Gluten-Free Living",
            category: "Dietary",
            prompt: "Celiac disease - strict gluten-free needed. Missing comfort foods like pasta, bread, and pizza. Want GF alternatives that actually taste good. Family doesn't need GF so prefer meals that work for everyone. Clear labeling of all ingredients is important."
        },
        {
            title: "🏃 Intermittent Fasting",
            category: "Lifestyle",
            prompt: "Following 16:8 eating window (noon to 8pm). Need satisfying lunch to break fast and substantial dinner. High protein and healthy fats to stay full. Black coffee and tea options for mornings. Want flexibility for weekend social events."
        },
        {
            title: "🍜 Asian Cuisine Fan",
            category: "Preferences",
            prompt: "Love Thai, Japanese, Korean, and Chinese flavors. Want authentic tastes using herbs, spices, ginger, garlic, and citrus. Enjoy both rice and noodle dishes. Like a balance of textures - crispy, soft, crunchy. Fresh ingredients and vibrant vegetables preferred."
        },
        {
            title: "🥗 Low-Carb Lifestyle",
            category: "Dietary",
            prompt: "Following a low-carb approach. Love cauliflower rice, zucchini noodles, and creative vegetable substitutes. Enjoy rich flavors from cheese, nuts, and avocados. Want satisfying meals that don't feel like 'diet food'. Prefer savory over sweet."
        },
        {
            title: "🌮 Mexican Food Lover",
            category: "Preferences",
            prompt: "Obsessed with Mexican and Tex-Mex cuisine. Love tacos, burritos, enchiladas, and fresh salsas. Want to explore authentic regional Mexican dishes. Enjoy spicy food and bold flavors. Cilantro, lime, and avocado are favorites. Weekend brunch ideas welcome."
        }
    ];

    // Loading overlay for preview generation
    const loadingOverlayForPreview: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
    };

    const spinnerStyle: React.CSSProperties = {
        width: "40px",
        height: "40px",
        border: "4px solid #f3f4f6",
        borderTop: "4px solid #3b82f6",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        margin: "0 auto",
        display: "block",
    };

    // Add CSS animation keyframes to the document if not already added
    React.useEffect(() => {
        if (!document.querySelector("#spinner-keyframes")) {
            const style = document.createElement("style");
            style.id = "spinner-keyframes";
            style.textContent = `
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    if (isPreviewMode && previewData) {
        return (
            <div style={{ ...containerStyle, position: "relative" }}>
                {isGeneratingPreview && (
                    <div style={loadingOverlayForPreview}>
                        <div
                            style={{
                                textAlign: "center",
                                padding: "48px",
                                backgroundColor: "white",
                                borderRadius: "16px",
                                boxShadow:
                                    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                                border: "1px solid #e5e7eb",
                                maxWidth: "400px",
                                width: "100%",
                                margin: "0 auto",
                            }}
                        >
                            <div style={spinnerStyle}></div>
                            <h3
                                style={{
                                    marginTop: "24px",
                                    marginBottom: "8px",
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    color: "#1f2937",
                                }}
                            >
                                Generating Preview
                            </h3>
                            <p
                                style={{
                                    margin: "0",
                                    color: "#6b7280",
                                    fontSize: "14px",
                                }}
                            >
                                This usually takes 3-30 seconds
                            </p>
                        </div>
                    </div>
                )}

                <div style={pageHeaderStyle}>
                    <h1 style={pageTitleStyle}>Meal Plan Preview</h1>
                    <p style={pageSubtitleStyle}>
                        Review and refine your meal plan before final generation
                    </p>
                    <button
                        onClick={handleBackToGenerate}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#3b82f6",
                            cursor: "pointer",
                            fontSize: "14px",
                            textDecoration: "underline",
                            marginTop: "8px",
                        }}
                    >
                        ← Back to Generate
                    </button>
                </div>

                {previewError && (
                    <div
                        style={{
                            ...cardStyle,
                            backgroundColor: "#fef2f2",
                            border: "2px solid #fecaca",
                            color: "#dc2626",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                            }}
                        >
                            <span style={{ fontSize: "20px" }}>⚠️</span>
                            <div>
                                <strong>Error:</strong> {previewError}
                            </div>
                        </div>
                    </div>
                )}

                {/* Preview Meals List */}
                <div style={cardStyle}>
                    <div style={sectionStyle}>
                        <label style={labelStyle}>📋 Proposed Meals</label>
                        <div
                            style={{
                                fontSize: "14px",
                                color: "#6b7280",
                                marginBottom: "16px",
                            }}
                        >
                            Toggle meals to accept ✅ or reject ❌. Rejected
                            meals will be replaced with alternatives.
                        </div>

                        <div
                            style={{
                                maxHeight: "400px",
                                overflow: "auto",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                backgroundColor: "white",
                            }}
                        >
                            {previewMeals.map((meal) => (
                                <div
                                    key={meal.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "12px 16px",
                                        borderBottom: "1px solid #f3f4f6",
                                        gap: "12px",
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "relative",
                                            width: "44px",
                                            height: "24px",
                                            backgroundColor: meal.rejected
                                                ? "#ef4444"
                                                : "#10b981",
                                            borderRadius: "12px",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                        }}
                                        onClick={() =>
                                            handlePreviewMealToggle(meal.id)
                                        }
                                    >
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "2px",
                                                left: meal.rejected
                                                    ? "2px"
                                                    : "22px",
                                                width: "20px",
                                                height: "20px",
                                                backgroundColor: "white",
                                                borderRadius: "50%",
                                                transition: "all 0.2s",
                                                boxShadow:
                                                    "0 2px 4px rgba(0, 0, 0, 0.1)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "12px",
                                            }}
                                        >
                                            {meal.rejected ? "❌" : "✅"}
                                        </div>
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontWeight: "500",
                                                fontSize: "14px",
                                                color: "#1f2937",
                                                marginBottom: "2px",
                                            }}
                                        >
                                            {meal.recipeName}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                color: "#6b7280",
                                            }}
                                        >
                                            Day {meal.dayNumber} •{" "}
                                            {meal.mealType}
                                        </div>
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="Optional notes..."
                                        value={meal.notes}
                                        onChange={(e) =>
                                            handlePreviewMealNotesChange(
                                                meal.id,
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: "200px",
                                            padding: "6px 8px",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                            outline: "none",
                                            opacity: meal.rejected ? 1 : 0.5,
                                        }}
                                        disabled={!meal.rejected}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Meal Plan Info */}
                <div style={cardStyle}>
                    <div style={sectionStyle}>
                        <div
                            style={{
                                ...collapsibleHeaderStyle,
                                background: showMealPlanInfo
                                    ? "#f0f9ff"
                                    : "#f8fafc",
                                borderColor: showMealPlanInfo
                                    ? "#3b82f6"
                                    : "#e2e8f0",
                                marginBottom: showMealPlanInfo ? "16px" : "0",
                                padding: "12px 16px",
                            }}
                            onClick={() =>
                                setShowMealPlanInfo(!showMealPlanInfo)
                            }
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#374151",
                                }}
                            >
                                📊 Meal Plan Info
                            </span>
                            <span
                                style={{
                                    ...chevronStyle,
                                    transform: showMealPlanInfo
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                }}
                            >
                                ▼
                            </span>
                        </div>

                        {showMealPlanInfo && previewData && (
                            <div
                                style={{
                                    padding: "0 4px",
                                    marginBottom: "16px",
                                }}
                            >
                                <pre style={preStyle}>
                                    {JSON.stringify(
                                        previewData.constraints,
                                        null,
                                        2
                                    )}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>

                {/* Additional Notes */}
                <div style={cardStyle}>
                    <div style={sectionStyle}>
                        <label style={labelStyle}>
                            ✍️ Additional Preferences
                        </label>
                        <textarea
                            value={previewNotes}
                            onChange={(e) => setPreviewNotes(e.target.value)}
                            placeholder="Any additional changes or preferences for the final meal plan..."
                            style={textAreaStyle}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={buttonContainerStyle}>
                    <button
                        style={
                            isGeneratingPreview
                                ? disabledButtonStyle
                                : secondaryButtonStyle
                        }
                        onClick={handleResubmitPreview}
                        disabled={isGeneratingPreview}
                        onMouseEnter={(e) => {
                            if (!isGeneratingPreview) {
                                e.currentTarget.style.backgroundColor =
                                    "#4b5563";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isGeneratingPreview) {
                                e.currentTarget.style.backgroundColor =
                                    "#64748b";
                            }
                        }}
                    >
                        <span>⚡</span>
                        Regenerate Preview
                    </button>

                    <button
                        style={
                            isGeneratingPreview
                                ? disabledButtonStyle
                                : primaryButtonStyle
                        }
                        onClick={handleGenerateFromPreview}
                        disabled={isGeneratingPreview}
                        onMouseEnter={(e) => {
                            if (!isGeneratingPreview) {
                                e.currentTarget.style.backgroundColor =
                                    "#2563eb";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isGeneratingPreview) {
                                e.currentTarget.style.backgroundColor =
                                    "#3b82f6";
                            }
                        }}
                    >
                        <span>🚀</span>
                        Generate Final Meal Plan
                    </button>
                </div>
            </div>
        );
    }

    const SectionHeader = ({ title, section, required = false, badge }: { title: string; section: string; required?: boolean; badge?: string }) => (
        <div
            onClick={() => toggleSection(section)}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: expandedSections.has(section) ? '16px' : '0',
                transition: 'all 0.2s ease',
                border: '1px solid #e2e8f0',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#1e293b' }}>
                    {title}
                </h3>
                {required && (
                    <span style={{
                        fontSize: '12px',
                        color: '#ef4444',
                        backgroundColor: '#fee2e2',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: 500,
                    }}>
                        Required
                    </span>
                )}
                {badge && (
                    <span style={{
                        fontSize: '12px',
                        color: '#3b82f6',
                        backgroundColor: '#dbeafe',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: 500,
                    }}>
                        {badge}
                    </span>
                )}
            </div>
            <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                    transform: expandedSections.has(section) ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                }}
            >
                <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );

    // Removed unused InfoTooltip

    const subscriptionOverlayStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
    };

    const subscriptionContentStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "40px",
        maxWidth: "600px",
    };

    const subscriptionTitleStyle: React.CSSProperties = {
        fontSize: "32px",
        fontWeight: "700",
        color: "#1a202c",
        marginBottom: "16px",
    };

    const subscriptionDescriptionStyle: React.CSSProperties = {
        fontSize: "18px",
        color: "#4a5568",
        marginBottom: "32px",
        lineHeight: "1.6",
    };

    const subscriptionButtonStyle: React.CSSProperties = {
        backgroundColor: "#3b82f6",
        color: "white",
        padding: "16px 48px",
        borderRadius: "8px",
        fontSize: "18px",
        fontWeight: "600",
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s",
        boxShadow: "0 4px 14px rgba(59, 130, 246, 0.3)",
    };

    const subscriptionFooterStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#718096",
        marginTop: "16px",
    };

    return (
        <div style={{ ...containerStyle, position: "relative" }}>
            {/* Subscription Overlay for users without access */}
            {!hasAccess && (
                <div style={subscriptionOverlayStyle}>
                    <div style={subscriptionContentStyle}>
                        <h2 style={subscriptionTitleStyle}>
                            {accessStatus.freeRunsRemaining === 0 && accessStatus.hasUsedFreeTrial
                                ? "Free Trial Ended"
                                : "Unlock AI Meal Planning"}
                        </h2>
                        <p style={subscriptionDescriptionStyle}>
                            {accessStatus.freeRunsRemaining === 0 && accessStatus.hasUsedFreeTrial
                                ? "You've used all your free meal plans. Subscribe to continue creating personalized meal plans."
                                : "Subscribe to access AI-powered meal planning and nutrition tracking."}
                        </p>
                        <button
                            onClick={openSubscriptionModal}
                            style={subscriptionButtonStyle}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#2563eb";
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.4)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#3b82f6";
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 4px 14px rgba(59, 130, 246, 0.3)";
                            }}
                        >
                            Subscribe Now
                        </button>
                        <p style={subscriptionFooterStyle}>
                            Cancel anytime • Instant access
                        </p>
                    </div>
                </div>
            )}

            {isGeneratingPreview && (
                <div style={loadingOverlayForPreview}>
                    <div
                        style={{
                            textAlign: "center",
                            padding: "48px",
                            backgroundColor: "white",
                            borderRadius: "16px",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                            border: "1px solid #e5e7eb",
                            maxWidth: "400px",
                            width: "100%",
                            margin: "0 auto",
                        }}
                    >
                        <div style={spinnerStyle}></div>
                        <h3
                            style={{
                                marginTop: "24px",
                                marginBottom: "8px",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#1f2937",
                            }}
                        >
                            Generating Preview
                        </h3>
                        <p
                            style={{
                                margin: "0",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}
                        >
                            This usually takes 3-30 seconds
                        </p>
                    </div>
                </div>
            )}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden',
                maxWidth: '1000px',
                margin: '0 auto',
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px',
                    borderBottom: '1px solid #e5e7eb',
                    background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
                }}>
                    <h2 style={{ fontSize: '24px', fontWeight: 700, margin: 0, color: '#1e293b' }}>
                        Generate Meal Plan
                    </h2>
                    <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>
                        Create a personalized meal plan with AI-powered nutrition guidance
                    </p>
                    {isUsingFreeTrial && (
                        <div style={{ marginTop: '16px' }}>
                            <FreeRunsIndicator />
                        </div>
                    )}
                </div>

                {/* Form */}
                <div style={{ padding: '24px' }}>
                    {/* Refinement Model Warning - show if not using accurate preset */}
                    {(!apiConfig.refinementModel || apiConfig.refinementModel !== APP_CONFIG.ACCURATE_REFINEMENT_MODEL) && (
                        <div style={{
                            marginBottom: '24px',
                            padding: '16px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #fcd34d',
                            display: 'flex',
                            alignItems: 'start',
                            gap: '12px',
                        }}>
                            <svg 
                                width="24" 
                                height="24" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                style={{ flexShrink: 0, marginTop: '2px' }}
                            >
                                <path 
                                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" 
                                    fill="#f59e0b"
                                />
                            </svg>
                            <div>
                                <p style={{ 
                                    margin: 0, 
                                    color: '#92400e',
                                    fontSize: '14px',
                                    lineHeight: '1.5'
                                }}>
                                    <strong>Tip:</strong> For casual meal planning, you're all set! However, if you're looking for meal plans that 
                                    consistently and accurately hit specific macro targets (ideal for athletes or fitness enthusiasts), 
                                    consider enabling <strong>Accuracy Mode</strong> in your{' '}
                                    <Link 
                                        to="/profile/config" 
                                        style={{ 
                                            color: '#d97706', 
                                            textDecoration: 'underline',
                                            fontWeight: '600'
                                        }}
                                    >
                                        AI Configuration
                                    </Link>.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Profile Selection Section */}
                    <div style={{ marginBottom: '24px' }}>
                        <SectionHeader 
                            title="👤 Profile Selection" 
                            section="profile" 
                            {...(selectedProfiles.length === 0 ? { required: true } : {})}
                        />
                        
                        {/* Always render ProfileSelector to ensure default profile loads, but hide when collapsed */}
                        <div style={{ 
                            padding: '0 4px',
                            display: expandedSections.has('profile') ? 'block' : 'none'
                        }}>
                            <ProfileSelector
                                limit={1}
                                onSelectionChange={handleProfileSelection}
                            />
                            
                            {selectedProfiles.length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <div
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#f0f9ff',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            color: '#0369a1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span>
                                            <strong>{selectedProfiles[0].name}</strong> selected
                                            {selectedProfiles[0].profile_data?.dietary_restrictions?.length > 0 && 
                                                ` • ${selectedProfiles[0].profile_data.dietary_restrictions.join(', ')}`
                                            }
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setShowProfileData(!showProfileData)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#0369a1',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            {showProfileData ? 'Hide' : 'View'} Full Profile
                                        </button>
                                    </div>
                                    
                                    {showProfileData && (
                                        <div style={{
                                            marginTop: '12px',
                                            padding: '12px',
                                            backgroundColor: '#f8fafc',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                        }}>
                                            <pre style={{
                                                margin: 0,
                                                fontSize: '12px',
                                                color: '#475569',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            }}>
                                                {JSON.stringify(selectedProfiles[0], null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div style={{
                                marginTop: '12px',
                                display: 'flex',
                                gap: '16px',
                                fontSize: '13px',
                            }}>
                                <button
                                    type="button"
                                    onClick={() => navigate("/profile/profiles")}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#3b82f6',
                                        cursor: 'pointer',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <span>👤</span> Manage Profiles
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate("/profile/config", { state: { activeTab: "api" } })}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#3b82f6',
                                        cursor: 'pointer',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <span>⚙️</span> AI Settings
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Meal Plan Duration Section - More Prominent */}
                    <div style={{ marginBottom: '24px' }}>
                        <SectionHeader 
                            title="📅 Meal Plan Duration" 
                            section="duration" 
                            required={false}
                            badge={`${numberOfDays} days`} 
                        />
                        
                        {expandedSections.has('duration') && (
                            <div style={{ padding: '0 4px' }}>
                                <div style={{
                                    backgroundColor: '#f0f9ff',
                                    border: '2px solid #3b82f6',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    textAlign: 'center',
                                }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        marginBottom: '16px',
                                    }}>
                                        How many days should your meal plan cover?
                                    </label>
                                    
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '16px',
                                        marginBottom: '16px',
                                    }}>
                                        <button
                                            type="button"
                                            onClick={() => setNumberOfDays(Math.max(1, numberOfDays - 1))}
                                            disabled={numberOfDays <= 1 || isGenerating}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                border: '2px solid #3b82f6',
                                                backgroundColor: 'white',
                                                color: '#3b82f6',
                                                fontSize: '20px',
                                                fontWeight: 'bold',
                                                cursor: numberOfDays > 1 ? 'pointer' : 'not-allowed',
                                                opacity: numberOfDays > 1 ? 1 : 0.5,
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            -
                                        </button>
                                        
                                        <div style={{
                                            fontSize: '48px',
                                            fontWeight: 700,
                                            color: '#3b82f6',
                                            minWidth: '100px',
                                        }}>
                                            {numberOfDays}
                                        </div>
                                        
                                        <button
                                            type="button"
                                            onClick={() => setNumberOfDays(Math.min(7, numberOfDays + 1))}
                                            disabled={numberOfDays >= 7 || isGenerating}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                border: '2px solid #3b82f6',
                                                backgroundColor: 'white',
                                                color: '#3b82f6',
                                                fontSize: '20px',
                                                fontWeight: 'bold',
                                                cursor: numberOfDays < 7 ? 'pointer' : 'not-allowed',
                                                opacity: numberOfDays < 7 ? 1 : 0.5,
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            +
                                        </button>
                                    </div>
                                    
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#64748b',
                                    }}>
                                        {numberOfDays === 1 && "Perfect for trying out a single day"}
                                        {numberOfDays === 2 && "Great for a weekend meal plan"}
                                        {numberOfDays === 3 && "A long weekend of healthy eating"}
                                        {numberOfDays === 4 && "Almost a work week covered"}
                                        {numberOfDays === 5 && "A full work week meal plan"}
                                        {numberOfDays === 6 && "Nearly a complete week"}
                                        {numberOfDays === 7 && "A complete week of meal planning"}
                                    </div>
                                    
                                    {/* Quick select buttons */}
                                    <div style={{
                                        marginTop: '16px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '8px',
                                    }}>
                                        {[1, 3, 5, 7].map(days => (
                                            <button
                                                key={days}
                                                type="button"
                                                onClick={() => setNumberOfDays(days)}
                                                style={{
                                                    padding: '6px 16px',
                                                    borderRadius: '20px',
                                                    border: `1px solid ${numberOfDays === days ? '#3b82f6' : '#e2e8f0'}`,
                                                    backgroundColor: numberOfDays === days ? '#3b82f6' : 'white',
                                                    color: numberOfDays === days ? 'white' : '#64748b',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                {days} {days === 1 ? 'day' : 'days'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Macro Targeting Section */}
                    <div style={{ marginBottom: '24px' }}>
                        <SectionHeader 
                            title="🎯 Macro Nutrient Targeting" 
                            section="macros" 
                            badge="Override Profile"
                        />
                        
                        {expandedSections.has('macros') && (
                            <div style={{ padding: '0 4px' }}>
                                <div style={{
                                    padding: '12px',
                                    backgroundColor: '#fef3c7',
                                    borderRadius: '8px',
                                    marginBottom: '16px',
                                    fontSize: '13px',
                                    color: '#92400e',
                                }}>
                                    ⚠️ Enabling macro targets here will override any targets set in your profile for this meal plan only.
                                </div>
                                <MacroTargeting 
                                    onChange={(targets) => {
                                        setMacroTargets(targets);
                                        setShowMacroTargeting(Object.keys(targets).length > 0);
                                    }}
                                    initialValues={macroTargets}
                                />
                            </div>
                        )}
                    </div>

                    {/* Instructions & Preferences Section */}
                    <div style={{ marginBottom: '24px' }}>
                        <SectionHeader 
                            title="✍️ Instructions & Preferences" 
                            section="instructions" 
                            {...(userInput.trim() === '' ? { required: true } : {})}
                        />
                        
                        {expandedSections.has('instructions') && (
                            <div style={{ padding: '0 4px' }}>
                                <textarea
                                            style={{
                                                width: '100%',
                                                minHeight: '200px',
                                                padding: '12px',
                                                border: `2px solid ${userInput.trim() === '' ? '#ef4444' : '#e2e8f0'}`,
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                            }}
                                            placeholder={placeholderText}
                                            value={userInput}
                                            onChange={(e) => setUserInput(e.target.value)}
                                            disabled={isGenerating}
                                        />
                                        
                                        {/* Example Prompts */}
                                        <div style={{ marginTop: '12px' }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                marginBottom: '8px' 
                                            }}>
                                                <label style={{ 
                                                    fontSize: '13px', 
                                                    color: '#64748b',
                                                    fontWeight: 500,
                                                }}>
                                                    Need inspiration?
                                                </label>
                                                <select
                                                    onChange={(e) => {
                                                        const selectedPrompt = examplePrompts.find(p => p.title === e.target.value);
                                                        if (selectedPrompt) {
                                                            setUserInput(selectedPrompt.prompt);
                                                        }
                                                    }}
                                                    style={{
                                                        flex: 1,
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #e2e8f0',
                                                        fontSize: '13px',
                                                        backgroundColor: 'white',
                                                        cursor: 'pointer',
                                                        outline: 'none',
                                                    }}
                                                    defaultValue=""
                                                >
                                                    <option value="" disabled>Select an example prompt...</option>
                                                    {Object.entries(
                                                        examplePrompts.reduce((acc, prompt) => {
                                                            if (!acc[prompt.category]) acc[prompt.category] = [];
                                                            acc[prompt.category].push(prompt);
                                                            return acc;
                                                        }, {} as Record<string, typeof examplePrompts>)
                                                    ).map(([category, prompts]) => (
                                                        <optgroup key={category} label={category}>
                                                            {prompts.map((prompt) => (
                                                                <option key={prompt.title} value={prompt.title}>
                                                                    {prompt.title}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                
                                {userInput.trim() === '' && (
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#dc2626',
                                        marginTop: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}>
                                        <span>⚠️</span>
                                        Instructions are required to generate a personalized meal plan
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: '#fee2e2',
                            border: '2px solid #fecaca',
                            borderRadius: '8px',
                            marginBottom: '24px',
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                color: '#dc2626',
                            }}>
                                <span style={{ fontSize: '20px' }}>⚠️</span>
                                <div>
                                    <strong>Error:</strong> {error}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end',
                        paddingTop: '24px',
                        borderTop: '1px solid #e5e7eb',
                    }}>
                        <button
                            style={{
                                padding: '12px 24px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: 'white',
                                color: '#374151',
                                fontSize: '16px',
                                fontWeight: 500,
                                cursor: selectedProfiles.length > 0 && userInput.trim() !== '' && !isGenerating ? 'pointer' : 'not-allowed',
                                opacity: selectedProfiles.length > 0 && userInput.trim() !== '' && !isGenerating ? 1 : 0.5,
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                            onClick={handleGeneratePreview}
                            disabled={isGenerating || selectedProfiles.length === 0 || userInput.trim() === ''}
                        >
                            <span>⚡</span>
                            Generate Preview
                        </button>

                        <button
                            style={{
                                padding: '12px 32px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                fontSize: '16px',
                                fontWeight: 600,
                                cursor: selectedProfiles.length > 0 && userInput.trim() !== '' && !isGenerating ? 'pointer' : 'not-allowed',
                                opacity: selectedProfiles.length > 0 && userInput.trim() !== '' && !isGenerating ? 1 : 0.5,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                            onClick={handleGenerateMealPlan}
                            disabled={isGenerating || selectedProfiles.length === 0 || userInput.trim() === ''}
                        >
                            <span>🚀</span>
                            Generate Meal Plan
                        </button>
                    </div>
                </div>
            </div>



            {/* Background Task Modal */}
            {showBackgroundModal && (
                <div style={loadingOverlayStyle}>
                    <div style={loadingBoxStyle}>
                        <div style={{ marginBottom: "20px" }}>
                            <span style={{ fontSize: "48px" }}>🤖</span>
                        </div>
                        <h3
                            style={{
                                fontSize: "20px",
                                fontWeight: 600,
                                marginBottom: "12px",
                            }}
                        >
                            Meal Plan Generation Started
                        </h3>
                        <p style={{ color: "#6b7280", marginBottom: "20px" }}>
                            Your meal plan is being generated in the background.
                            This may take a few minutes - it will appear in your history upon completion.
                        </p>
                        <p
                            style={{
                                color: "#6b7280",
                                fontSize: "14px",
                                marginBottom: "24px",
                            }}
                        >
                            You can continue using the app while waiting. We'll
                            notify you when it's ready!
                        </p>
                        <button
                            style={{
                                backgroundColor: "#3b82f6",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "10px 24px",
                                fontSize: "16px",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                            }}
                            onClick={() => setShowBackgroundModal(false)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    "#2563eb";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    "#3b82f6";
                            }}
                        >
                            Hide
                        </button>
                    </div>
                </div>
            )}

            {/* Free Trial Modal */}
            {showFreeTrialModal && (
                <div style={loadingOverlayStyle}>
                    <div style={{
                        ...loadingBoxStyle,
                        maxWidth: "450px",
                    }}>
                        <div style={{ marginBottom: "20px" }}>
                            <span style={{ fontSize: "48px" }}>🚦</span>
                        </div>
                        <h3
                            style={{
                                fontSize: "20px",
                                fontWeight: 600,
                                marginBottom: "12px",
                                color: "#1f2937",
                            }}
                        >
                            Free Trial Limitation
                        </h3>
                        <p style={{ color: "#6b7280", marginBottom: "20px", lineHeight: "1.6" }}>
                            Free trial users can only run one task at a time. 
                            Please wait for your current task to complete before starting a new one.
                        </p>
                        <p
                            style={{
                                color: "#6b7280",
                                fontSize: "14px",
                                marginBottom: "24px",
                                lineHeight: "1.6",
                            }}
                        >
                            To run multiple tasks simultaneously, please subscribe to our premium plan.
                        </p>
                        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                            <button
                                style={{
                                    backgroundColor: "#6b7280",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 24px",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "background-color 0.2s",
                                }}
                                onClick={() => setShowFreeTrialModal(false)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#4b5563";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#6b7280";
                                }}
                            >
                                OK
                            </button>
                            <button
                                style={{
                                    backgroundColor: "#3b82f6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 24px",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "background-color 0.2s",
                                }}
                                onClick={() => {
                                    setShowFreeTrialModal(false);
                                    navigate("/profile", { state: { activeTab: "billing" } });
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#2563eb";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#3b82f6";
                                }}
                            >
                                Upgrade to Premium
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
